import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { AnyAction } from 'redux'
import { NearestBoreholeObject, NearestBoreholes } from 'src/dataSources/geo/NearestBoreHoles'
import { Borehole } from 'src/dataSources/geo/Borehole'
import { BoreholeLegend, BoreholeLegendObject } from 'src/dataSources/geo/BoreholeLegend'
import { RootState } from 'src/Store'
import { AddressReverse, AddressReverseResult } from 'src/dataSources/dataforsyningen/AddressReverse'
import { EmptyAction, DataAction, MessageAction } from 'src/CustomTypes'

export const FETCHING_BOREHOLE_STARTED = 'BOREHOLE_INFO_ACTIONS_FETCHING_BOREHOLE_STARTED'
export const FETCHING_BOREHOLE_SUCCESS = 'BOREHOLE_INFO_ACTIONS_FETCHING_BOREHOLE_SUCCESS'
export const FETCHING_BOREHOLE_FAILURE = 'BOREHOLE_INFO_ACTIONS_FETCHING_BOREHOLE_FAILURE'
export const FETCHING_ADDRESS_STARTED = 'BOREHOLE_INFO_ACTIONS_FETCHING_ADDRESS_STARTED'
export const FETCHING_ADDRESS_SUCCESS = 'BOREHOLE_INFO_ACTIONS_FETCHING_ADDRESS_SUCCESS'
export const FETCHING_ADDRESS_FAILURE = 'BOREHOLE_INFO_ACTIONS_FETCHING_ADDRESS_FAILURE'
export const FETCHING_IMAGE_STARTED = 'BOREHOLE_INFO_ACTIONS_FETCHING_IMAGE_STARTED'
export const FETCHING_IMAGE_SUCCESS = 'BOREHOLE_INFO_ACTIONS_FETCHING_IMAGE_SUCCESS'
export const FETCHING_IMAGE_FAILURE = 'BOREHOLE_INFO_ACTIONS_FETCHING_IMAGE_FAILURE'
export const FETCHING_LEGEND_STARTED = 'BOREHOLE_INFO_ACTIONS_FETCHING_LEGEND_STARTED'
export const FETCHING_LEGEND_SUCCESS = 'BOREHOLE_INFO_ACTIONS_FETCHING_LEGEND_SUCCESS'
export const FETCHING_LEGEND_FAILURE = 'BOREHOLE_INFO_ACTIONS_FETCHING_LEGEND_FAILURE'
export const CLEAR_STATE = 'BOREHOLE_INFO_ACTIONS_CLEAR_STATE'

interface BoreholeInfoActions {
  fetchingBoreholeStarted: () => EmptyAction
  fetchingBoreholeFailure: (message: string) => MessageAction
  fetchingBoreholeSuccess: (borehole: NearestBoreholeObject | null) => DataAction<NearestBoreholeObject | null>
  fetchingAddressStarted: () => EmptyAction
  fetchingAddressFailure: (message: string) => MessageAction
  fetchingAddressSuccess: (address: AddressReverseResult) => DataAction<AddressReverseResult>
  fetchingImageStarted: () => EmptyAction
  fetchingImageFailure: (message: string) => MessageAction
  fetchingImageSuccess: (data: string) => DataAction<string>
  fetchingLegendStarted: () => EmptyAction
  fetchingLegendFailure: (message: string) => MessageAction
  fetchingLegendSuccess: (data: BoreholeLegendObject) => DataAction<BoreholeLegendObject>
  clearState: () => EmptyAction

  // Thunk actions.
  setCoordinate: (coords: number[]) => ThunkAction<Promise<void>, RootState, {}, AnyAction>
  setBoreholeId: (id: number) => ThunkAction<Promise<void>, RootState, {}, AnyAction>
}

export const boreholeInfoActions: BoreholeInfoActions = {
  fetchingBoreholeStarted: (): EmptyAction => {
    return {
      type: FETCHING_BOREHOLE_STARTED
    }
  },
  fetchingBoreholeFailure: (message: string): MessageAction => {
    return {
      type: FETCHING_BOREHOLE_FAILURE,
      message: message
    }
  },
  fetchingBoreholeSuccess: (borehole: NearestBoreholeObject | null): DataAction<NearestBoreholeObject | null> => {
    return {
      type: FETCHING_BOREHOLE_SUCCESS,
      data: borehole
    }
  },
  fetchingAddressStarted: (): EmptyAction => {
    return {
      type: FETCHING_ADDRESS_STARTED
    }
  },
  fetchingAddressFailure: (message: string): MessageAction => {
    return {
      type: FETCHING_ADDRESS_FAILURE,
      message: message
    }
  },
  fetchingAddressSuccess: (address: AddressReverseResult): DataAction<AddressReverseResult> => {
    return {
      type: FETCHING_ADDRESS_SUCCESS,
      data: address
    }
  },
  fetchingImageStarted: (): EmptyAction => {
    return {
      type: FETCHING_IMAGE_STARTED
    }
  },
  fetchingImageFailure: (message: string) => {
    return {
      type: FETCHING_IMAGE_FAILURE,
      message: message
    }
  },
  fetchingImageSuccess: (data: string) => {
    return {
      type: FETCHING_IMAGE_SUCCESS,
      data: data
    }
  },
  fetchingLegendStarted: (): EmptyAction => {
    return {
      type: FETCHING_LEGEND_STARTED
    }
  },
  fetchingLegendFailure: (message: string) => {
    return {
      type: FETCHING_LEGEND_FAILURE,
      message: message
    }
  },
  fetchingLegendSuccess: (data: BoreholeLegendObject) => {
    return {
      type: FETCHING_LEGEND_SUCCESS,
      data: data
    }
  },
  clearState: () => {
    return {
      type: CLEAR_STATE
    }
  },
  setCoordinate: (coords: number[]): ThunkAction<Promise<void>, RootState, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<RootState, {}, AnyAction>, getState: () => RootState): Promise<void> => {
      // Clear current state.
      dispatch(boreholeInfoActions.clearState())

      // Fetch the nearest borehole.
      const token = getState().authState.token
      dispatch(boreholeInfoActions.fetchingBoreholeStarted())
      const wsNearestBoreholes = new NearestBoreholes(token)
      wsNearestBoreholes.setCoordinate(coords)
      let boreholes: NearestBoreholeObject[] = []
      try {
        boreholes = await wsNearestBoreholes.fetchBoreHoles()
        dispatch(boreholeInfoActions.fetchingBoreholeSuccess(boreholes.length > 0 ? boreholes[0] : null))
        if (boreholes.length === 0) {
          return // Stop execution here - we cannot fetch anymore without a borehole.
        }
      } catch (err) {
        console.error(err)
        const errorMessage: string = (err as Error).message
        dispatch(boreholeInfoActions.fetchingBoreholeFailure(errorMessage))
        return // Stop execution here - we cannot fetch anymore without the borehole.
      }

      // Get ready to fetch address info, borehole image, and legend.
      // --- Postal address ---
      const wsAddressReverse = new AddressReverse()
      wsAddressReverse.setCoords(coords)
      dispatch(boreholeInfoActions.fetchingAddressStarted())
      wsAddressReverse.fetchReverseAddress()
        .then((addressInfo: AddressReverseResult) => {
          dispatch(boreholeInfoActions.fetchingAddressSuccess(addressInfo))
        })
        .catch(err => {
          console.error(err)
          const errorMessage: string = (err as Error).message
          dispatch(boreholeInfoActions.fetchingAddressFailure(errorMessage))
        })

      // --- Borehole image ---
      const borehole: NearestBoreholeObject = boreholes[0]
      const wsBorehole = new Borehole(token)
      wsBorehole.setBoreholeNo(borehole.BoreholeNumber)
      dispatch(boreholeInfoActions.fetchingImageStarted())
      wsBorehole.fetchBorehole()
        .then(image => {
          dispatch(boreholeInfoActions.fetchingImageSuccess(image))
        })
        .catch(err => {
          console.error(err)
          const errorMessage: string = (err as Error).message
          dispatch(boreholeInfoActions.fetchingImageFailure(errorMessage))
        })

      // --- Borehole legend ---
      const wsBoreholeLegend = new BoreholeLegend(token)
      wsBoreholeLegend.setBoreholeNo(borehole.BoreholeNumber)
      dispatch(boreholeInfoActions.fetchingLegendStarted())
      wsBoreholeLegend.fetchBoreholeLegend()
        .then(legend => {
          dispatch(boreholeInfoActions.fetchingLegendSuccess(legend))
        })
        .catch(err => {
          console.error(err)
          const errorMessage: string = (err as Error).message
          dispatch(boreholeInfoActions.fetchingLegendFailure(errorMessage))
        })
    }
  },
  setBoreholeId: (id: number): ThunkAction<Promise<void>, RootState, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<RootState, {}, AnyAction>, getState: () => RootState): Promise<void> => {
      // Clear current state.
      dispatch(boreholeInfoActions.clearState())

      dispatch(boreholeInfoActions.fetchingBoreholeStarted())
      dispatch(boreholeInfoActions.fetchingBoreholeSuccess({
        BoreholeId: id,
        BoreholeNumber: '-',
        X: 0,
        Y: 0,
        Espg: 0,
        DistanceToBorehole: 0,
        DatasourceId: 0
      }))

      // Fetch the nearest borehole.
      const token = getState().authState.token

      // --- Borehole image ---
      const wsBorehole = new Borehole(token)
      wsBorehole.setBoreholeId(id)
      dispatch(boreholeInfoActions.fetchingImageStarted())
      wsBorehole.fetchBorehole()
        .then(image => {
          dispatch(boreholeInfoActions.fetchingImageSuccess(image))
        })
        .catch(err => {
          console.error(err)
          const errorMessage: string = (err as Error).message
          dispatch(boreholeInfoActions.fetchingImageFailure(errorMessage))
        })

      // --- Borehole legend ---
      const wsBoreholeLegend = new BoreholeLegend(token)
      wsBoreholeLegend.setBoreholeId(id)
      dispatch(boreholeInfoActions.fetchingLegendStarted())
      wsBoreholeLegend.fetchBoreholeLegend()
        .then(legend => {
          dispatch(boreholeInfoActions.fetchingLegendSuccess(legend))
        })
        .catch(err => {
          console.error(err)
          const errorMessage: string = (err as Error).message
          dispatch(boreholeInfoActions.fetchingLegendFailure(errorMessage))
        })
    }
  }
}
